import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Shield, AlertTriangle, CheckCircle2, Clock, Users, Heart, FileText, Activity, Lock, TrendingUp, Building2, ChevronDown } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_agency-network-hub/artifacts/d66f2cg2_IMG_0019.png";

function ScoreGauge({ score, label }) {
  const color = score >= 80 ? '#A8E6CF' : score >= 60 ? '#FCD34D' : '#FF6F61';
  return (
    <div className="text-center">
      <div className="relative w-20 h-20 mx-auto">
        <svg viewBox="0 0 36 36" className="w-20 h-20">
          <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#333" strokeWidth="2" />
          <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
            fill="none" stroke={color} strokeWidth="2.5" strokeDasharray={`${score}, 100`} strokeLinecap="round" />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center text-lg font-bold" style={{ color }}>{score}%</span>
      </div>
      <p className="text-[11px] text-[#999] mt-1">{label}</p>
    </div>
  );
}

function StatCard({ value, label, alert, icon: Icon }) {
  return (
    <Card className="bg-[#1A2329] border-[#2A3439]">
      <CardContent className="p-4 text-center">
        <Icon className="w-5 h-5 mx-auto mb-1 text-[#666]" />
        <p className={`text-2xl font-bold ${alert ? 'text-[#FF6F61]' : 'text-[#E5E7EB]'}`}>{value}</p>
        <p className="text-[10px] text-[#999]">{label}</p>
      </CardContent>
    </Card>
  );
}

function Section({ title, icon: Icon, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-[#2A3439] rounded-xl overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-3 p-4 bg-[#1A2329] hover:bg-[#1F2B31] transition-colors text-left" data-testid={`section-${title.toLowerCase().replace(/\s+/g, '-')}`}>
        <Icon className="w-5 h-5 text-[#00A6A0]" />
        <span className="text-sm font-medium text-[#E5E7EB] flex-1">{title}</span>
        <ChevronDown className={`w-4 h-4 text-[#666] transition-transform ${open ? '' : '-rotate-90'}`} />
      </button>
      {open && <div className="p-4 bg-[#0F1419] space-y-3">{children}</div>}
    </div>
  );
}

export default function InspectorDashboard() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [complaints, setComplaints] = useState(null);
  const [safeguarding, setSafeguarding] = useState(null);
  const [risks, setRisks] = useState(null);
  const [docs, setDocs] = useState(null);
  const [breaches, setBreaches] = useState(null);
  const [training, setTraining] = useState(null);

  useEffect(() => {
    if (token) fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const { data: d } = await axios.get(`${API}/api/inspector/view/${token}`);
      setData(d);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired inspector token');
    }
  };

  const loadSection = async (section, setter) => {
    if (setter === null) return;
    try {
      const { data: d } = await axios.get(`${API}/api/inspector/view/${token}/${section}`);
      setter(d);
    } catch { /* ignore */ }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#0F1419] flex items-center justify-center p-4">
        <Card className="bg-[#1A2329] border-[#2A3439] max-w-md w-full">
          <CardContent className="p-8 text-center">
            <Lock className="w-12 h-12 text-[#FF6F61] mx-auto mb-4" />
            <h1 className="text-xl font-bold text-[#E5E7EB] mb-2">Access Denied</h1>
            <p className="text-sm text-[#999]">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#0F1419] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#00A6A0] border-t-transparent rounded-full" />
      </div>
    );
  }

  const s = data.component_scores || {};

  return (
    <div className="min-h-screen bg-[#0F1419] text-[#E5E7EB]" data-testid="inspector-dashboard">
      {/* Header */}
      <header className="border-b border-[#2A3439] bg-[#1A2329]">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="FendanaCura" className="h-8 w-auto" />
            <div>
              <h1 className="text-lg font-bold text-[#00A6A0]">CQC Inspection Dashboard</h1>
              <p className="text-[11px] text-[#999]">Read-only compliance view</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm font-medium">{data.organization}</p>
            <p className="text-[10px] text-[#999]">{new Date(data.report_date).toLocaleDateString('en-GB')} | Period: {data.period}</p>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* CQC Readiness Score */}
        <Card className="bg-[#1A2329] border-[#2A3439]" data-testid="inspector-cqc-score">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="text-center">
                <p className="text-xs text-[#999] uppercase tracking-wider mb-1">CQC Inspection Readiness</p>
                <p className={`text-5xl font-bold ${data.cqc_readiness_score >= 80 ? 'text-[#A8E6CF]' : data.cqc_readiness_score >= 60 ? 'text-[#FCD34D]' : 'text-[#FF6F61]'}`}>
                  {data.cqc_readiness_score}%
                </p>
              </div>
              <div className="flex gap-6 flex-wrap justify-center">
                <ScoreGauge score={s.visit_completion || 0} label="Visits" />
                <ScoreGauge score={s.training_compliance || 0} label="Training" />
                <ScoreGauge score={s.complaint_response || 0} label="Complaints" />
                <ScoreGauge score={s.medication_safety || 0} label="Medication" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard value={data.workforce?.clients || 0} label="Clients" icon={Heart} />
          <StatCard value={data.workforce?.caregivers || 0} label="Caregivers" icon={Users} />
          <StatCard value={`${data.visits?.completion_rate || 0}%`} label="Visit Completion" icon={CheckCircle2} />
          <StatCard value={data.visits?.missed || 0} label="Missed Visits" icon={AlertTriangle} alert={data.visits?.missed > 0} />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard value={data.complaints?.open || 0} label="Open Complaints" icon={FileText} alert={data.complaints?.open > 0} />
          <StatCard value={data.safeguarding?.open || 0} label="Open Safeguarding" icon={Shield} alert={data.safeguarding?.open > 0} />
          <StatCard value={data.incidents?.total_this_period || 0} label="Incidents (Period)" icon={AlertTriangle} />
          <StatCard value={`${data.training?.compliance_rate || 0}%`} label="Training Compliance" icon={TrendingUp} />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard value={data.risk_assessments?.active || 0} label="Active Risk Assessments" icon={Activity} />
          <StatCard value={data.duty_of_candour?.total || 0} label="Duty of Candour" icon={FileText} />
          <StatCard value={data.ig_compliance?.breaches?.open || 0} label="Open Breaches" icon={Lock} alert={data.ig_compliance?.breaches?.open > 0} />
          <StatCard value={data.ig_compliance?.dsars?.overdue || 0} label="Overdue DSARs" icon={Clock} alert={data.ig_compliance?.dsars?.overdue > 0} />
        </div>

        {/* Expandable Sections */}
        <Section title="Complaints Register" icon={FileText} defaultOpen={false}>
          {!complaints ? (
            <button onClick={() => loadSection('complaints', setComplaints)} className="text-sm text-[#00A6A0] hover:underline" data-testid="load-complaints">Load complaints data</button>
          ) : complaints.length === 0 ? (
            <p className="text-sm text-[#999]">No complaints recorded</p>
          ) : (
            <div className="space-y-2">
              {complaints.map(c => (
                <div key={c.id} className="bg-[#1A2329] p-3 rounded-lg border border-[#2A3439]">
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-[#FCD34D]">{c.category}</span>
                    <span className={c.status === 'closed' || c.status === 'responded' ? 'text-[#A8E6CF]' : 'text-[#FF6F61]'}>{c.status}</span>
                    <span className="text-[#666]">Received: {c.date_received}</span>
                    <span className="text-[#666]">Deadline: {c.statutory_deadline}</span>
                  </div>
                  {c.description && <p className="text-xs text-[#999] mt-1">{c.description?.slice(0, 200)}</p>}
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section title="Safeguarding Concerns" icon={Shield}>
          {!safeguarding ? (
            <button onClick={() => loadSection('safeguarding', setSafeguarding)} className="text-sm text-[#00A6A0] hover:underline" data-testid="load-safeguarding">Load safeguarding data</button>
          ) : safeguarding.length === 0 ? (
            <p className="text-sm text-[#999]">No safeguarding concerns</p>
          ) : (
            <div className="space-y-2">
              {safeguarding.map(s => (
                <div key={s.id} className="bg-[#1A2329] p-3 rounded-lg border border-[#2A3439]">
                  <div className="flex items-center gap-2 text-xs flex-wrap">
                    <span className="text-[#FF6F61]">{s.concern_type}</span>
                    <span className={s.status === 'closed' ? 'text-[#A8E6CF]' : 'text-[#FCD34D]'}>{s.status}</span>
                    <span className="text-[#666]">{s.created_at?.slice(0, 10)}</span>
                    {s.cqc_notified && <span className="text-[#A8E6CF]">CQC Notified</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section title="Risk Assessments" icon={Activity}>
          {!risks ? (
            <button onClick={() => loadSection('risks', setRisks)} className="text-sm text-[#00A6A0] hover:underline" data-testid="load-risks">Load risk assessment data</button>
          ) : risks.length === 0 ? (
            <p className="text-sm text-[#999]">No active risk assessments</p>
          ) : (
            <div className="space-y-2">
              {risks.map(r => (
                <div key={r.id} className="bg-[#1A2329] p-3 rounded-lg border border-[#2A3439] flex items-center justify-between">
                  <div className="text-xs">
                    <span className="text-[#E5E7EB] font-medium">{r.client_name}</span>
                    <span className="text-[#666] ml-2">{r.assessment_type}</span>
                  </div>
                  <div className="text-xs">
                    <span className={`px-2 py-0.5 rounded-full ${r.risk_level === 'high' ? 'bg-[#FF6F61]/10 text-[#FF6F61]' : r.risk_level === 'medium' ? 'bg-[#FCD34D]/10 text-[#FCD34D]' : 'bg-[#A8E6CF]/10 text-[#A8E6CF]'}`}>
                      {r.risk_level} ({r.total_score})
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section title="Duty of Candour" icon={FileText}>
          {!docs ? (
            <button onClick={() => loadSection('duty-of-candour', setDocs)} className="text-sm text-[#00A6A0] hover:underline" data-testid="load-doc">Load Duty of Candour records</button>
          ) : docs.length === 0 ? (
            <p className="text-sm text-[#999]">No Duty of Candour records</p>
          ) : (
            <div className="space-y-2">
              {docs.map(d => (
                <div key={d.id} className="bg-[#1A2329] p-3 rounded-lg border border-[#2A3439] text-xs">
                  <span className="text-[#E5E7EB]">{d.client_name}</span>
                  <span className="text-[#666] ml-2">{d.status}</span>
                  <span className="text-[#666] ml-2">{d.created_at?.slice(0, 10)}</span>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section title="Data Breaches" icon={Lock}>
          {!breaches ? (
            <button onClick={() => loadSection('breaches', setBreaches)} className="text-sm text-[#00A6A0] hover:underline" data-testid="load-breaches">Load breach register</button>
          ) : breaches.length === 0 ? (
            <p className="text-sm text-[#999]">No data breaches recorded</p>
          ) : (
            <div className="space-y-2">
              {breaches.map(b => (
                <div key={b.id} className="bg-[#1A2329] p-3 rounded-lg border border-[#2A3439]">
                  <div className="flex items-center gap-2 text-xs">
                    <span className={b.severity === 'critical' ? 'text-[#FF6F61]' : b.severity === 'high' ? 'text-[#FF6F61]' : 'text-[#FCD34D]'}>{b.severity?.toUpperCase()}</span>
                    <span className="text-[#999]">{b.status}</span>
                    {b.ico_overdue && <span className="text-[#FF6F61] font-medium">ICO OVERDUE</span>}
                    <span className="text-[#666]">{b.detected_date}</span>
                  </div>
                  <p className="text-xs text-[#999] mt-1">{b.description?.slice(0, 150)}</p>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section title="Training Compliance" icon={TrendingUp}>
          {!training ? (
            <button onClick={() => loadSection('training', setTraining)} className="text-sm text-[#00A6A0] hover:underline" data-testid="load-training">Load training data</button>
          ) : (
            <div className="space-y-3">
              <p className="text-xs text-[#999]">{training.training_documents?.length || 0} mandatory training documents</p>
              <div className="space-y-1">
                {training.staff_compliance?.map((s, i) => (
                  <div key={i} className="flex items-center justify-between bg-[#1A2329] p-2 rounded-lg border border-[#2A3439] text-xs">
                    <span className="text-[#E5E7EB]">{s.name}</span>
                    <span className={s.compliance_rate >= 100 ? 'text-[#A8E6CF]' : s.compliance_rate >= 50 ? 'text-[#FCD34D]' : 'text-[#FF6F61]'}>
                      {s.completed}/{s.total_required} ({s.compliance_rate}%)
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Section>

        {/* Footer */}
        <div className="text-center py-6 border-t border-[#2A3439]">
          <p className="text-[10px] text-[#666]">This is a read-only compliance view generated by FendanaCura. Data is live and reflects the current state of the organisation{"'"}s records.</p>
          <p className="text-[10px] text-[#666] mt-1">IG Compliance: DPO {data.ig_compliance?.dpo_configured ? 'Configured' : 'Not Configured'}</p>
        </div>
      </main>
    </div>
  );
}
